import request from '.'
import User from './user'

/**
 * Expands an object map of ID to Domain to an array of inflated Domain objects.
 */
function expandDomains(info) {
  return Object.entries(info).map(
    ([id, info]) => new Domain(parseInt(id, 10), info)
  )
}

export default class Domain {
  static async fetchById(id) {
    const info = await request(`/api/admin/domains/${id}`)
    return new Domain(id, info)
  }

  static async fetchMine() {
    const info = await request('/api/stats/my_domains')
    return expandDomains(info)
  }

  static async fetchAll(options) {
    const route = options.extended ? `/api/admin/domains` : `/api/domains`
    const info = await request(route)

    if (options.extended) {
      // Turn { id: domain } into an array of domains with IDs.
      return expandDomains(info)
    } else {
      const bareInfo = info

      // Official domain IDs are stored in a separate object.
      return Object.entries(bareInfo.domains).map(([id, domain]) => ({
        domain,
        id: parseInt(id, 10),
        official: bareInfo.officialdomains.includes(parseInt(id, 10)),
      }))
    }
  }

  static async create({ domain, adminOnly, official }) {
    await request('/api/admin/domains', {
      body: JSON.stringify({
        admin_only: adminOnly,
        domain,
        official,
      }),
      method: 'PUT',
    })
  }

  constructor(id, response) {
    const {
      info: {
        domain,
        official,
        admin_only: adminOnly,
        cf_enabled: cfEnabled,
        owner,
      },
      stats,
      public_stats: publicStats,
    } = response

    Object.assign(this, {
      adminOnly,
      cfEnabled,
      domain,
      id,
      official,
      owner: owner == null ? null : new User(owner),
      publicStats,
      stats,
    })
  }

  get validDomain() {
    return this.domain.replace('*', 'wildcard')
  }

  toString() {
    return this.domain
  }

  async delete() {
    await request(`/api/admin/domains/${this.id}`, { method: 'DELETE' })
  }

  async edit({ owner, adminOnly, ...options }) {
    const route = `/api/admin/domains/${this.id}`

    if (owner && !this.owner) {
      await request(`${route}/owner`, {
        body: JSON.stringify({ owner_id: owner.id }),
        method: 'PUT',
      })
    }

    await request(route, {
      body: JSON.stringify({
        ...options,
        ...(adminOnly != null ? { admin_only: adminOnly } : {}),
        ...(owner != null && this.owner ? { owner_id: owner.id } : {}),
      }),
      method: 'PATCH',
    })
  }

  async setOwner(user) {
    if (!this.owner) {
      return
    }

    await request(`/api/admin/domains/${this.id}`, {
      body: JSON.stringify({
        owner_id: user.id,
      }),
      method: 'PATCH',
    })
  }
}
